// Candidate-consent error copy. `message` is the toast copy; `tooltip` (when present) is the
// shorter form shown on a disabled action, and falls back to `message` when absent.
// Source: product copy sheet (candidate-consent).
const consent = {
  err_consent_feature_unavailable: {
    message: "Consent isn't available on your Kula account",
  },
  err_consent_not_enabled: {
    message: "Consent isn't turned on for your account. Enable it in Settings.",
  },
  // Legacy catch-all — kept for older responses; request/remind now return the
  // per-case codes below (CORE-4890).
  err_consent_not_requestable: {
    message: "Consent can't be requested for this candidate",
  },
  // Request per-case reasons.
  err_consent_already_requested: {
    message: 'A consent request is already pending. Send a reminder instead.',
    tooltip: 'A consent request is already pending',
  },
  err_consent_already_granted: {
    message: 'This candidate has already granted consent',
  },
  err_consent_blocked: {
    message: "This candidate has declined three times and can't be asked again",
  },
  err_consent_marked_not_required: {
    message: 'This candidate has been manually marked as granted consent',
  },
  err_consent_template_missing: {
    message: 'No consent email template set up. Add one in Settings.',
    tooltip: 'No consent email template is set up',
  },
  // Reminder per-case reasons.
  err_consent_no_pending_request: {
    message: 'No pending consent request to remind. Request consent first.',
    tooltip: 'No consent request is pending',
  },
  err_consent_reminder_limit_reached: {
    message: 'All three reminders have already been sent',
  },
  err_consent_reminder_too_soon: {
    message: 'A reminder was sent in the last 24 hours. Try again later.',
    tooltip: 'Another reminder is available 24 hours after the last one',
  },
  err_consent_reminder_not_allowed: {
    message: "This reminder can't be sent right now",
  },
  // Settings: consent email body must carry the {{consent.link}} liquid token.
  err_consent_link_token_missing: {
    message:
      'The consent email is missing the {{consent.link}} token. Add it to save the template.',
  },
  // Bulk-request per-item outcomes.
  err_contact_not_found: {
    message: 'Candidate no longer exists',
  },
  err_internal: {
    message: 'Something went wrong. Try again later',
  },
  err_consent_not_grantable: {
    message:
      "This candidate declined consent, so it can't be marked as granted",
  },
  err_consent_not_required_when_refused: {
    message: 'A refused consent cannot be marked as not required.',
  },
  err_consent_not_withdrawable: {
    message: 'This candidate has no active consent to withdraw',
  },
  err_consent_not_extendable: {
    message: 'Only granted consents can be extended.',
  },
  err_consent_processing_blocked: {
    message:
      "This candidate hasn't given consent, so this action isn't allowed",
  },
  err_already_anonymized: {
    message: 'This candidate has already been anonymized',
  },
  err_confirm_required_active_applications: {
    message: 'This candidate has active applications. Confirm to anonymize.',
  },
  err_empty_targets: {
    message: 'No candidates selected. Select at least one to request consent.',
  },
  // 403 raised on every write against an anonymized (GDPR-erased) candidate.
  err_contact_anonymized: {
    message: "This candidate has been anonymized and can't be edited",
  },
  // Settings validation.
  err_mode_required: {
    message: 'Select a consent mode to turn it on',
  },
  // Settings duration_config validation (validity_config / data_retention_config).
  err_invalid_value: {
    message: 'Enter a consent validity between 1 and 999',
  },
  err_invalid_unit: {
    message: 'Select days, months, or years',
  },
  // Policy text exceeds the storage limit (matched as a prefix — the BE appends the byte limit).
  err_text_size_cannot_be_greater_than: {
    message: 'The privacy policy text is too long. Shorten it and try again.',
  },
  err_url_must_be_a_valid_http_or_https_url: {
    message: 'Enter a valid URL starting with http:// or https://',
  },
  err_file_size_limit_exceeded: {
    message: 'The file is over 5 MB. Upload a smaller PDF.',
  },
  // Email offset out of range (prefix-matched — the BE appends the limit).
  err_offset_value_must_be_less_than_or_equal_to: {
    message: 'Email timing must be 999 or less.',
  },
  // Rules API — office/rule validation.
  err_office_already_in_rule: {
    message: 'One or more of these offices already belong to another rule.',
  },
  // Doubled err_ prefix is the real wire value on the contract path (global_default + offices).
  err_ats_office_ids_err_global_default_should_not_have_office: {
    message: "A global default rule can't also target specific offices.",
  },
  // Extend-validity contract (value+unit XOR no_expiry).
  err_no_expiry_or_value_and_unit_required: {
    message: 'Set a validity period, or choose never expires.',
  },
  err_no_expiry_mutually_exclusive_with_value_and_unit: {
    message: 'Choose either a validity period or never expires, not both.',
  },
  // Summary-digest cadence validation.
  err_frequency_must_be_one_of_daily_weekly_monthly: {
    message: 'Choose a digest frequency of daily, weekly, or monthly.',
  },
  // Summary-digest settings validation (recipients + cadence).
  err_base_no_attributes: {
    message: 'Make a change before saving.',
  },
  err_recipients_required: {
    message: 'Add at least one recipient to turn the summary on.',
  },
  err_invalid_user_id: {
    message: 'One or more selected users are no longer in your account.',
  },
  err_invalid_role_id: {
    message: 'One or more selected roles are no longer in your account.',
  },
  err_invalid_email: {
    message: 'Enter valid email addresses for external recipients.',
  },
  err_unit_must_be_one_of_day_week_month: {
    message: 'Choose a cadence of day, week, or month.',
  },
  err_value_must_be_greater_than_or_equal_to_1: {
    message: 'The summary cadence must be at least 1.',
  },
  err_value_must_be_less_than_or_equal_to_90: {
    message: 'The summary cadence must be 90 or less.',
  },
  err_enabled_must_be_boolean: {
    message: "Couldn't save consent settings. Please try again.",
  },
};

export default consent;
